import { CoherencePanel } from '@coherence-design-system/controls';
import { Context } from '@micro-frontend-react/employee-experience/lib/Context';
import { IEmployeeExperienceContext } from '@micro-frontend-react/employee-experience/lib/IEmployeeExperienceContext';
import { DatePicker } from '@micro-frontend-react/employee-experience/lib/DatePicker';
import {
  getClickFeature,
  getFeature,
} from '@micro-frontend-react/employee-experience/lib/UsageTelemetryHelper';
import { TextField } from '@micro-frontend-react/employee-experience/lib/TextField';
import { ComboBox, IComboBoxOption } from 'office-ui-fabric-react';
import { MessageBar } from 'office-ui-fabric-react/lib/MessageBar';
import { Link } from '../../Share.styled';
import { PrimaryButton } from 'office-ui-fabric-react/lib/components/Button';
import { Label } from 'office-ui-fabric-react/lib/Label';
import * as React from 'react';
import { useContext, useState } from 'react';
import {
  ControlsWithValues,
  Dictionary,
  DictionaryComboOptions,
} from '../../../Models/DictionaryTypes';
import {
  ILetterGenerationRequest,
  ILetterSubmissionRequest,
} from '../../../models/ILetterGenerationRequest';
import { ILetterTemplate } from '../../../models/ILetterTemplate';
import { ILetterToken, ILetterTokenPresetValues } from '../../../models/ILetterToken';
import { requestLetterGeneration } from '../../../redux/actions/metadataActions';
import { useLetterMetadata } from '../../../redux/hooks/useLetterMetadata';
import { MetadataContext } from '../../../shared/MetadataContext';
import { formatMMDDYYYY } from '../../../Utils/formatDateString';
import * as styled from './MetadataPanel.styled';
import * as WindowSize from '../../../redux/hooks/useWindowDimensions';
import { CustomDropDown } from './MetadataPanel.styled';
import { EventType, UserEvent } from '@micro-frontend-react/employee-experience/lib/UsageTelemetry';

export interface IMetadataPanelProps {
  selectedLetter: ILetterTemplate;
  isGenerationRestricted: boolean;
}

export interface ICustomTdProps {
  controlType: string;
  windowWidth: number;
}

export const MetadataPanel = React.memo(function MetadataPanelContent(
  props: IMetadataPanelProps
): React.ReactElement {
  const { selectedLetter, isGenerationRestricted } = props;
  const { telemetryClient } = useContext(Context as React.Context<IEmployeeExperienceContext>);

  const feature = getFeature('EVL', 'MetadataPanel');

  const [
    letterMetadata,
    letterPdf,
    hasError,
    isLoading,
    errorMessage,
    isLoadingLetterGeneration,
    hasErrorLetterGeneration,
    errorMessageLetterGeneration,
  ] = useLetterMetadata(feature, selectedLetter);

  const mobileScreenWidth = 729;
  const { width, height } = WindowSize.useWindowDimensions();

  const [mandatoryTokensDictionary, setMandatoryTokensDictionary] = useState<Dictionary>();
  const [controlsWithValues, setControlsWithValues] = useState<ControlsWithValues>();
  const [dictionaryDropDowns, setDictionaryDropDowns] = useState<DictionaryComboOptions>();
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
  const { dispatch } = React.useContext(Context as React.Context<IEmployeeExperienceContext>);

  React.useEffect(() => {
    const dictionary: Dictionary = {};
    const dictionaryComboOptions: DictionaryComboOptions = {};
    const dictionaryControlsWithValues: ControlsWithValues = {};

    letterMetadata?.endUserTokens?.map((token: ILetterToken, _) => {
      if (token.isMandatory) dictionary[token.name] = true;
    });

    letterMetadata?.endUserTokens?.map((token: ILetterToken, _) => {
      const options: IComboBoxOption[] = [];
      token.tokenPresetValues?.map((presetValues: ILetterTokenPresetValues, _) => {
        const option: IComboBoxOption = {
          key: `${token.name}_${presetValues.label}`,
          text: presetValues.label,
          data: {
            label: presetValues.label,
            presetDisplayValue: presetValues.presetDisplayValue,
            presetData: presetValues.presetData,
          },
        };
        options.push(option);
      });

      dictionaryComboOptions[token.name] = options;
    });

    letterMetadata?.automatedTokens?.map((token: ILetterToken, _) => {
      dictionaryControlsWithValues[token.name] = {
        textValue: token.value,
        dateValue: undefined,
        dropDownValue: null,
      };
    });

    dictionaryControlsWithValues['comments'] = null;

    letterMetadata?.endUserTokens?.map((token: ILetterToken, _) => {
      dictionaryControlsWithValues[token.name] = null;
    });

    setMandatoryTokensDictionary(dictionary);
    setDictionaryDropDowns(dictionaryComboOptions);
    setControlsWithValues(dictionaryControlsWithValues);

    return () => {
      setControlsWithValues({});
      setMandatoryTokensDictionary({});
      setDictionaryDropDowns({});
      setHasSubmitted(false);
    };
  }, [letterMetadata]);

  return (
    <>
      <MetadataContext.Consumer>
        {(context) => (
          <CoherencePanel
            isOpen={context.showMetadataPanel}
            onDismiss={() => {
              context.toggleMetadataPanel(false);
            }}
            customWidth={`900px`}
            isLightDismiss={true}
            titleText={`${selectedLetter?.name}`}
            closeButtonAriaLabel="Close panel"
            hasCloseButton={true}
            overlayProps={{
              isDarkThemed: true,
            }}
            telemetryHook={telemetryClient}
          >
            <p>{selectedLetter?.detailedDescription}</p>
            <br />
            <styled.RestrictedFormWrapper>
            {isGenerationRestricted && (
              <styled.RestrictedOverlay>
                <MessageBar
                  isMultiline={true}
                  styles={{
                    root: { width: '100%', backgroundColor: '#e6f2ff', justifyContent: 'center' },
                    iconContainer: { display: 'none' },
                    text: { fontSize: 14, lineHeight: '22px', textAlign: 'center', width: '100%' },
                    innerText: { textAlign: 'center', width: '100%' },
                  }}
                >
                  <span aria-hidden="true">📢</span> Hello, this letter is now available on{' '}
                  <a href={__ESS_URL__} target="_blank" rel="noopener noreferrer" style={{ color: '#0078d4', fontWeight: 600, textDecoration: 'underline' }}>
                    Employee Self-Service
                  </a>
                  . Please use the prompt &quot;Generate {selectedLetter?.name} letter for me.&quot;
                </MessageBar>
              </styled.RestrictedOverlay>
            )}
            <styled.GenerationForm
              disabled={isGenerationRestricted}
              isGenerationRestricted={isGenerationRestricted}
              aria-disabled={isGenerationRestricted}
            >
              <styled.PlaceholderSidePanelContainer>
              {isLoading &&
                [...Array(3)].map(() => (
                  <>
                    <styled.TemplatesPlaceHolder height={30} width={45} />
                    <styled.TemplatesPlaceHolder height={30} width={38} />
                    <styled.BreakFlexDiv />
                    <styled.TemplatesPlaceHolder height={30} width={32} />
                    <styled.TemplatesPlaceHolder height={30} width={55} />
                    <styled.BreakFlexDiv />
                  </>
                ))}
              {!isLoading && !hasError && (
                <>
                  <styled.TokensTable aria-hidden="true" role="presentation">
                    {letterMetadata?.automatedTokens?.map((token: ILetterToken, _) => (
                      <>
                        <tr aria-hidden="true" role="presentation">
                          <td>{token.displayName}</td>
                          <td>
                            <Label>{token.value}</Label>
                          </td>
                        </tr>
                      </>
                    ))}
                  </styled.TokensTable>

                  <styled.NewControlsTable aria-hidden="true" role="presentation">
                    {letterMetadata?.endUserTokens?.map((token: ILetterToken, _) => (
                      <>
                        <styled.ControlsTableRow aria-hidden="true" role="presentation">
                          <styled.ControlsTableData>{token.displayName}</styled.ControlsTableData>
                          <styled.CustomTd
                            controlType={token.uiControlType}
                            windowWidth={width}
                            aria-label={token.displayName}
                          >
                            {
                              {
                                TextArea: (
                                  <>
                                    <TextField
                                      name={token.name}
                                      aria-hidden={true}
                                      role={'textbox'}
                                      multiline={true}
                                      rows={parseInt(__TextArea_Row_Size__)}
                                      ariaLabel={
                                        token.displayName + (token.isMandatory ? ` ,required` : '')
                                      }
                                      title={
                                        token.displayName + (token.isMandatory ? ` ,required` : '')
                                      }
                                      onChange={(id, value) => {
                                        setControlsWithValues((prevState) => ({
                                          ...prevState,
                                          [id]: {
                                            textValue: value,
                                            dateValue: undefined,
                                            dropDownValue: null,
                                          },
                                        }));
                                        if (token.isMandatory) {
                                          setMandatoryTokensDictionary((prevState) => ({
                                            ...prevState,
                                            [id]: value === '' ? true : false,
                                          }));
                                        }
                                      }}
                                      styles={{
                                        root: {
                                          maxWidth: 450,
                                        },
                                      }}
                                      label=""
                                      required={token.isMandatory}
                                      {...getClickFeature(feature, 'TextArea')}
                                    />
                                    <div role="region" aria-live="polite">
                                      {token.isMandatory &&
                                        hasSubmitted &&
                                        mandatoryTokensDictionary[token.name] && (
                                          <styled.FormErrorValidationMessage>
                                            {`${token.displayName} is mandatory`}
                                          </styled.FormErrorValidationMessage>
                                        )}
                                    </div>
                                  </>
                                ),
                                TextBox: (
                                  <>
                                    <TextField
                                      name={token.name}
                                      aria-hidden={true}
                                      role={'textbox'}
                                      ariaLabel={
                                        token.displayName + (token.isMandatory ? `,required` : '')
                                      }
                                      title={
                                        token.displayName + (token.isMandatory ? ` ,required` : '')
                                      }
                                      onChange={(id, value) => {
                                        setControlsWithValues((prevState) => ({
                                          ...prevState,
                                          [id]: {
                                            textValue: value,
                                            dateValue: undefined,
                                            dropDownValue: null,
                                          },
                                        }));
                                        if (token.isMandatory) {
                                          setMandatoryTokensDictionary((prevState) => ({
                                            ...prevState,
                                            [id]: value === '' ? true : false,
                                          }));
                                        }
                                      }}
                                      styles={{
                                        root: {
                                          maxWidth: 450,
                                        },
                                      }}
                                      label=""
                                      required={token.isMandatory}
                                      {...getClickFeature(feature, 'TextBox')}
                                    />
                                    <div role="region" aria-live="polite">
                                      {token.isMandatory &&
                                        hasSubmitted &&
                                        mandatoryTokensDictionary[token.name] && (
                                          <styled.FormErrorValidationMessage>
                                            {`${token.displayName} is mandatory`}
                                          </styled.FormErrorValidationMessage>
                                        )}
                                    </div>
                                  </>
                                ),
                                DatePicker: (
                                  <>
                                    <DatePicker
                                      id={token.name}
                                      name={token.name}
                                      label=""
                                      ariaLabel={
                                        token.displayName + (token.isMandatory ? ` ,required` : '')
                                      }
                                      title={
                                        token.displayName + (token.isMandatory ? ` ,required` : '')
                                      }
                                      aria-label={
                                        token.isMandatory ? `required datepicker` : 'datepicker'
                                      }
                                      aria-roledescription={
                                        token.isMandatory ? `required datepicker` : 'datepicker'
                                      }
                                      aria-hidden={true}
                                      role={'textbox'}
                                      isRequired={token.isMandatory}
                                      onChange={(id, value) => {
                                        setControlsWithValues((prevState) => ({
                                          ...prevState,
                                          [id]: {
                                            textValue: null,
                                            dateValue: formatMMDDYYYY(value),
                                            dropDownValue: null,
                                          },
                                        }));
                                        if (token.isMandatory) {
                                          setMandatoryTokensDictionary((prevState) => ({
                                            ...prevState,
                                            [id]: value === null ? true : false,
                                          }));
                                        }
                                      }}
                                      styles={{
                                        root: {
                                          maxWidth: 450,
                                        },
                                      }}
                                      value={
                                        controlsWithValues[token.name]?.dateValue != null &&
                                        controlsWithValues[token.name]?.dateValue != ''
                                          ? new Date(controlsWithValues[token.name]?.dateValue)
                                          : undefined
                                      }
                                      usageEvent={{
                                        feature: 'EVL',
                                        subFeature: 'MetadataPanel.DatePicker',
                                      }}
                                    />

                                    <div role="region" aria-live="polite">
                                      {token.isMandatory &&
                                        hasSubmitted &&
                                        mandatoryTokensDictionary[token.name] && (
                                          <styled.FormErrorValidationMessage>
                                            {`${token.displayName} is mandatory`}
                                          </styled.FormErrorValidationMessage>
                                        )}
                                    </div>
                                  </>
                                ),
                                DropDown: (
                                  <styled.CustomDropDownWrapper>
                                    <CustomDropDown
                                      id={token.name}
                                      label=""
                                      windowWidth={width}
                                      windowHeight={height}
                                      className={'mandatoryComboBox'}
                                      ariaLabel={
                                        token.displayName + (token.isMandatory ? ` ,required` : '')
                                      }
                                      aria-roledescription={
                                        token.isMandatory ? `required dropdown` : 'dropdown'
                                      }
                                      aria-hidden={true}
                                      title={
                                        token.displayName + (token.isMandatory ? ` ,required` : '')
                                      }
                                      role={'textbox'}
                                      onChange={(event, option) => {
                                        const splittedKey = option.key.toString().split('_');

                                        setControlsWithValues((prevState) => ({
                                          ...prevState,
                                          [splittedKey[0]]: {
                                            textValue: null,
                                            dateValue: undefined,
                                            dropDownValue: option.data,
                                          },
                                        }));
                                        if (token.isMandatory) {
                                          setMandatoryTokensDictionary((prevState) => ({
                                            ...prevState,
                                            [splittedKey[0]]:
                                              splittedKey[1] === null ||
                                              splittedKey[1] === undefined
                                                ? true
                                                : false,
                                          }));
                                        }
                                      }}
                                      allowFreeform={false}
                                      autoComplete={'on'}
                                      options={dictionaryDropDowns[token.name]}
                                      styles={{
                                        root: {
                                          maxWidth: 500,
                                        },
                                        optionsContainerWrapper: {
                                          maxWidth: '400px',
                                          maxHeight: '300px',
                                        },
                                      }}
                                      onRenderOption={(option: IComboBoxOption): JSX.Element => {
                                        return (
                                          <div
                                            style={{
                                              height: 'auto',
                                              minHeight: 20,
                                              margin: 5,
                                            }}
                                          >
                                            <span
                                              style={{
                                                fontWeight: 600,
                                              }}
                                            >
                                              {option.text}
                                            </span>
                                            <br />
                                            <span>
                                              {
                                                (option.data as ILetterTokenPresetValues)
                                                  .presetDisplayValue
                                              }
                                            </span>
                                          </div>
                                        );
                                      }}
                                    />
                                    {token.isMandatory && (
                                      <span className={'mandatory'} aria-label="required">
                                        *
                                      </span>
                                    )}
                                    <div role="region" aria-live="polite">
                                      {token.isMandatory &&
                                        hasSubmitted &&
                                        mandatoryTokensDictionary[token.name] && (
                                          <styled.FormErrorValidationMessage>
                                            {`${token.displayName} is mandatory`}
                                          </styled.FormErrorValidationMessage>
                                        )}
                                    </div>
                                  </styled.CustomDropDownWrapper>
                                ),
                              }[token.uiControlType]
                            }
                          </styled.CustomTd>
                        </styled.ControlsTableRow>
                      </>
                    ))}
                  </styled.NewControlsTable>

                  {selectedLetter?.requiresManualProcessing && (
                    <>
                      <styled.OptionalCommentsContainer>
                        <styled.OptionalCommentsLabel>Optional</styled.OptionalCommentsLabel>
                      </styled.OptionalCommentsContainer>
                      <styled.ControlsTable
                        aria-hidden="true"
                        role="presentation"
                        windowWidth={width}
                        windowHeight={height}
                      >
                        <tr>
                          <td>{'Additional comments for HR'}</td>
                          <td>
                            <>
                              <TextField
                                id={'comments'}
                                name={'comments'}
                                aria-hidden={false}
                                role={'textbox'}
                                title={'Additional comments for HR'}
                                onChange={(id, value) => {
                                  setControlsWithValues((prevState) => ({
                                    ...prevState,
                                    [id]: {
                                      textValue: value,
                                      dateValue: undefined,
                                      dropDownValue: null,
                                    },
                                  }));
                                }}
                                label=""
                                required={false}
                                styles={{
                                  root: {
                                    maxWidth: 450,
                                  },
                                }}
                                {...getClickFeature(feature, 'TextBox')}
                              />
                            </>
                          </td>
                        </tr>
                      </styled.ControlsTable>
                    </>
                  )}
                </>
              )}

              {!isLoading && hasError && (
                <styled.SidePanelErrorContainer>
                  <styled.ErrorLoadingTokens
                    src={`./assets/NotFoundLogo.png`}
                    role="presentation"
                  />
                  <Label>Oops! Something went wrong while fetching metadata.</Label>
                </styled.SidePanelErrorContainer>
              )}
              </styled.PlaceholderSidePanelContainer>
              <br />
              <p>
              Review your information, ensure that your personal information is displayed correctly
              in your letter. You can update it through{' '}
              <Link
                to={__Employee_Central_Link__}
                title="Employee Central"
                target="_blank"
                usageEvent={{
                  feature: 'EVL',
                  subFeature: 'MetadataPanel.EmployeeCentralLink',
                }}
              >
                Employee Central (EC)
              </Link>{' '}
              or submit{' '}
              <Link
                to={
                  context.userProfile != null && context.userProfile.allowedAva
                    ? __Ava_Link__
                    : __AskHR_Application__Link__
                }
                title="AskHR Link"
                target="_blank"
                usageEvent={{
                  feature: 'EVL',
                  subFeature: 'MetadataPanel.AskHRLink',
                }}
              >
                AskHR
              </Link>{' '}
              ticket if necessary.
              </p>
              <br />
              {!isLoading && !hasError && (
              <PrimaryButton
                title={`Submit EVL`}
                text={selectedLetter?.requiresManualProcessing ? 'Preview' : 'Submit'}
                style={{ marginRight: 10, marginBottom: 15 }}
                disabled={isLoading || isGenerationRestricted}
                onClick={() => {
                  if (isGenerationRestricted) return;

                  setHasSubmitted(true);

                  let isFormValid = true;

                  Object.keys(mandatoryTokensDictionary).forEach((key) => {
                    if (mandatoryTokensDictionary[key] === true) {
                      isFormValid = false;
                    }
                  });

                  if (isFormValid) {
                    const requestBody: ILetterGenerationRequest = {
                      templateID: parseInt(selectedLetter.id),
                      companyCode: '',
                      letterTokens: [],
                      askhrNotes: controlsWithValues['comments']?.textValue,
                      userTimeOffset: new Date().getTimezoneOffset(),
                    };

                    Object.keys(controlsWithValues).forEach((key) => {
                      const value = controlsWithValues[key];

                      if (key != 'comments') {
                        if (value?.textValue != null && value?.textValue != undefined)
                          requestBody.letterTokens.push({
                            name: key,
                            value: value.textValue,
                            selectedPresetValue: null,
                          });
                        else if (value?.dateValue != null && value?.dateValue != undefined)
                          requestBody.letterTokens.push({
                            name: key,
                            value: value.dateValue,
                            selectedPresetValue: null,
                          });
                        else if (value?.dropDownValue != null && value?.dropDownValue != undefined)
                          requestBody.letterTokens.push({
                            name: key,
                            value: null,
                            selectedPresetValue: {
                              label: value.dropDownValue.label,
                              presetData: value.dropDownValue.presetData,
                              presetDisplayValue: value.dropDownValue.presetDisplayValue,
                            },
                          });
                      }
                    });

                    const requestPreviewBody: ILetterSubmissionRequest = {
                      templateID: parseInt(selectedLetter.id),
                      alias: '',
                      letterTokens: requestBody.letterTokens,
                      askhrNotes: requestBody.askhrNotes,
                      userTimeOffset: new Date().getTimezoneOffset(),
                    };

                    dispatch(requestLetterGeneration(feature, requestBody));

                    context.setSubmissionRequest(requestPreviewBody);
                    context.toggleMetadataPanel(false);
                    context.togglePdfPanel(true);

                    const linkEvent: UserEvent = {
                      feature: 'EVL',
                      subFeature: 'Home.MetadataPanel.SubmitButton',
                      subFeatureLevel2: '',
                      featureLocation: 'Home',
                      eventName: 'LetterPreviewRequested',
                      type: EventType.User,
                    };

                    telemetryClient.trackEvent(linkEvent, {
                      SelectedLetterId: selectedLetter?.id,
                      SelectedLetterName: selectedLetter?.name,
                    });
                  }
                }}
                />
              )}
            </styled.GenerationForm>
            </styled.RestrictedFormWrapper>
          </CoherencePanel>
        )}
      </MetadataContext.Consumer>
    </>
  );
});
